import {
  useState,
  useRef,
  useEffect,
  type ChangeEvent,
  type KeyboardEvent,
  Dispatch,
  SetStateAction,
} from "react"
import { useTonConnectUI } from "@tonconnect/ui-react"
import { useNavigate } from "react-router-dom"

import { ProviderIcon } from "./sheetUtils"
import { useTonDeposit } from "./useTonDeposit"
import { BaseAmountSheet } from "./BaseAmountSheet"
import { useXRocketDeposit } from "./useXRocketDeposit"
import { useCryptoBotDeposit } from "./useCryptoBotDeposit"

import { useBalance } from "@/entities/user"

import { useI18n } from "@/shared/i18n"
import { useFeatureFlag } from "@/shared/features"
import { cn } from "@/shared/lib/utils"
import { formatBalance } from "@/shared/lib/utils"
import { useWalletBalance } from "@/shared/hooks/useWalletBalance"
import {
  formatTonDepositDisplay,
  maxTonDepositNano,
  parseTonDecimalToNano,
  tonPresetToNano,
} from "@/shared/lib/tonWalletDeposit"
import Button from "@/shared/ui/Button"
import { telegram } from "@/shared/lib/telegram"
import { getGiftRelayBotTelegramUrl } from "@/features/inventory/lib/relayBot"
import { GiftsEmptyHint } from "@/features/inventory/ui/GiftsEmptyHint"
import {
  AnalyticsEvent,
  setAnalyticsDepositUiSource,
  trackAnalyticsEvent,
  type DepositUiSource,
} from "@/shared/lib/analytics"

// ─── Types ────────────────────────────────────────────────────────────────────

type DepositProvider = "TON" | "CryptoBot" | "xRocket"
type DepositTab = "TON" | "Stars" | "Gifts" | "RUB"

// ─── Constants ────────────────────────────────────────────────────────────────

const PRESETS = [4, 7.5, 12, 25, 50]
const MAX_DEPOSIT_AMOUNT = 99999

const PROVIDER_LIST: Array<{ name: DepositProvider }> = [
  { name: "TON" },
  { name: "CryptoBot" },
  { name: "xRocket" },
]

// ─── Component ────────────────────────────────────────────────────────────────

export function DepositSheet({
  isOpen,
  setIsOpen,
  initialAmount,
  depositUiSource,
}: {
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
  /**
   * Optional TON amount to preselect when the sheet opens. Used by the
   * gift-withdrawal flow to seed the deposit with the exact shortfall.
   */
  initialAmount?: number | null
  /** Analytics: where the user opened the deposit sheet. */
  depositUiSource: DepositUiSource
}) {
  const { t } = useI18n()

  useEffect(() => {
    if (!isOpen) return
    setAnalyticsDepositUiSource(depositUiSource)
    trackAnalyticsEvent(AnalyticsEvent.DepositSheetOpen, {
      deposit_ui_source: depositUiSource,
    })
  }, [isOpen, depositUiSource])
  const navigate = useNavigate()
  const giftsEnabled = useFeatureFlag("gifts")

  const [tonConnectUI] = useTonConnectUI()

  const { data: balance } = useBalance()
  const balanceAmountStr = balance?.balance?.amount ?? "0"

  const {
    depositTon,
    isProcessing: depositProcessing,
    connectedAddress,
  } = useTonDeposit({
    onSuccess: () => setIsOpen(false),
  })

  const { data: chainWalletBalance } = useWalletBalance(
    connectedAddress || null,
    {
      enabled: isOpen,
    }
  )
  const walletBalanceNano = chainWalletBalance?.nano ?? null
  const walletBalanceTon = chainWalletBalance?.ton ?? null

  const { depositViaCryptoBot, isProcessing: isCryptoBotProcessing } =
    useCryptoBotDeposit({
      onSuccess: () => setIsOpen(false),
    })

  const { depositViaXRocket, isProcessing: isXRocketProcessing } =
    useXRocketDeposit({
      onSuccess: () => setIsOpen(false),
    })

  const providers = {
    CryptoBot: {
      onDeposit: depositViaCryptoBot,
      isProcessing: isCryptoBotProcessing,
    },
    xRocket: {
      onDeposit: depositViaXRocket,
      isProcessing: isXRocketProcessing,
    },
  }

  const [selectedTab, setSelectedTab] = useState<DepositTab>("TON")
  const [selectedProvider, setSelectedProvider] =
    useState<DepositProvider>("TON")
  const [selectedPreset, setSelectedPreset] = useState<number | null>(null)
  const [isMaxPreset, setIsMaxPreset] = useState(false)
  const isGiftsTab = giftsEnabled && selectedTab === "Gifts"

  useEffect(() => {
    if (!giftsEnabled && selectedTab === "Gifts") {
      setSelectedTab("TON")
    }
  }, [giftsEnabled, selectedTab])

  const openRelayBotChat = () => {
    telegram.openTelegramLink(getGiftRelayBotTelegramUrl())
  }

  const goToInventory = () => {
    setIsOpen(false)
    navigate("/inventory")
  }
  const [inputValue, setInputValue] = useState("")
  const amountInputRef = useRef<HTMLInputElement>(null)

  // Seed the amount whenever the sheet transitions to open with an
  // explicit `initialAmount`. Re-runs on each open so callers can change
  // the suggested amount between sessions (e.g. gift withdrawal shortfall).
  useEffect(() => {
    if (!isOpen) return
    if (typeof initialAmount !== "number" || initialAmount <= 0) return
    const clamped = Math.min(initialAmount, MAX_DEPOSIT_AMOUNT)
    setSelectedPreset(Math.round(clamped * 100) / 100)
    setIsMaxPreset(false)
    setInputValue("")
  }, [isOpen, initialAmount])

  const isTonMode = selectedProvider === "TON"
  const walletConnected = Boolean(
    connectedAddress && connectedAddress.length > 0
  )
  const activeHandler = isTonMode ? null : (providers[selectedProvider] ?? null)
  const activeIsProcessing = isTonMode
    ? depositProcessing
    : (activeHandler?.isProcessing ?? false)

  const maxDepositNano =
    walletBalanceNano != null ? maxTonDepositNano(walletBalanceNano) : null

  const effectiveAmountNano = (() => {
    if (isTonMode && isMaxPreset && maxDepositNano != null) {
      return maxDepositNano > 0 ? maxDepositNano : null
    }
    if (selectedPreset !== null) {
      return Math.min(tonPresetToNano(selectedPreset), MAX_DEPOSIT_AMOUNT * 1e9)
    }
    if (inputValue) return parseTonDecimalToNano(inputValue)
    return null
  })()

  const exceedsWallet =
    isTonMode &&
    walletConnected &&
    effectiveAmountNano != null &&
    maxDepositNano != null &&
    effectiveAmountNano > maxDepositNano

  const hasValidAmount =
    effectiveAmountNano != null &&
    effectiveAmountNano > 0 &&
    !exceedsWallet

  const inputDisplayValue = (() => {
    if (isMaxPreset && maxDepositNano != null && maxDepositNano > 0) {
      return formatTonDepositDisplay(maxDepositNano)
    }
    if (selectedPreset !== null) {
      const nano = Math.min(
        tonPresetToNano(selectedPreset),
        MAX_DEPOSIT_AMOUNT * 1e9
      )
      return formatTonDepositDisplay(nano)
    }
    return inputValue
  })()

  const amountWarning =
    exceedsWallet && maxDepositNano != null
      ? t("deposit.walletLimitWarning", {
          amount: formatTonDepositDisplay(maxDepositNano),
        })
      : null

  const handlePreset = (amount: number) => {
    setIsMaxPreset(false)
    setSelectedPreset(Math.min(amount, MAX_DEPOSIT_AMOUNT))
    setInputValue("")
    amountInputRef.current?.blur()
  }

  const handleMax = () => {
    if (maxDepositNano != null && maxDepositNano > 0) {
      setIsMaxPreset(true)
      setSelectedPreset(null)
      setInputValue("")
      amountInputRef.current?.blur()
    }
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const normalized = e.target.value.replace(",", ".").replace(/[^\d.]/g, "")
    const [intPartRaw, decimalPart] = normalized.split(".")
    const intPartTrimmed =
      intPartRaw.replace(/^0+(\d)/, "$1").slice(0, 5) ||
      (intPartRaw.length ? "0" : "")
    const hasDot = normalized.includes(".")
    const sanitized = hasDot
      ? `${intPartTrimmed}.${(decimalPart ?? "").slice(0, 2)}`
      : intPartTrimmed
    if (sanitized === "" || sanitized === "." || sanitized === "0.") {
      setInputValue(sanitized)
      setSelectedPreset(null)
      setIsMaxPreset(false)
      return
    }
    const parsed = parseFloat(sanitized)
    if (isNaN(parsed)) {
      setInputValue("")
      setSelectedPreset(null)
      setIsMaxPreset(false)
      return
    }
    setInputValue(sanitized)
    setSelectedPreset(null)
    setIsMaxPreset(false)
  }

  const handleAmountKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      amountInputRef.current?.blur()
    }
  }

  const handleDeposit = async () => {
    if (!hasValidAmount) return
    if (!isTonMode) {
      await activeHandler?.onDeposit(effectiveAmountNano! / 1e9)
      return
    }
    if (!walletConnected) return
    await depositTon(effectiveAmountNano!)
  }

  // ── Slots ──────────────────────────────────────────────────────────────────

  const topContent = (
    <>
      {/* Currency tabs */}
      <div className="mb-3 shrink-0 px-4">
        <div className="flex gap-0.5 rounded-full bg-[#7E7D9B]/[32%] p-1">
          {(["TON", "Stars", "Gifts", "RUB"] as const).map((tab) => {
            const enabled = tab === "TON" || (tab === "Gifts" && giftsEnabled)
            const active = selectedTab === tab
            return (
              <button
                key={tab}
                disabled={!enabled}
                onClick={() => enabled && setSelectedTab(tab)}
                className={cn(
                  "h-8 flex-1 rounded-full text-sm/none font-medium transition-colors",
                  active
                    ? "bg-[#7E7D9B] text-white"
                    : enabled
                      ? "text-white/70 active:text-white"
                      : "cursor-not-allowed text-white/[24%]"
                )}
              >
                {tab}
              </button>
            )
          })}
        </div>
      </div>

      {/* Provider selector — TON-only; a provider is enabled if it's TON or its handlers are passed */}
      {isGiftsTab ? null : (
      <div className="mb-5 flex shrink-0 justify-center gap-2 px-4">
        {PROVIDER_LIST.map((p) => {
          const available =
            p.name === "TON" ||
            !!providers[p.name as Exclude<DepositProvider, "TON">]
          const active = selectedProvider === p.name
          return (
            <button
              key={p.name}
              disabled={!available}
              onClick={() => {
                if (available) setSelectedProvider(p.name)
              }}
              className={cn(
                "flex h-8 items-center justify-center gap-1 rounded-full pl-1.5 pr-2.5 transition-colors",
                available && active
                  ? "bg-gray-100 text-white"
                  : available
                    ? "bg-gray-32 hover:bg-gray-100/50"
                    : "cursor-not-allowed bg-gray-32/50"
              )}
            >
              <ProviderIcon name={p.name} />
              <span className="text-[14px] font-medium leading-[17px] tracking-[-0.5px] text-inherit">
                {p.name}
              </span>
              {!available && (
                <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[9px] leading-none text-white/35">
                  {t("common.soon")}
                </span>
              )}
            </button>
          )
        })}
      </div>
      )}
    </>
  )

  // ── Gifts tab: inventory-style empty screen with relay-bot link ──────────────
  const giftBody = (
    <GiftsEmptyHint text={t("deposit.giftHint")} onHandleClick={openRelayBotChat} />
  )

  const giftCta = (
    <div className="flex gap-2">
      <Button
        appearance="tertiary"
        size="medium"
        className="flex-1"
        onClick={goToInventory}
      >
        {t("deposit.toInventory")}
      </Button>
      <Button
        appearance="green"
        size="medium"
        className="flex-1"
        onClick={openRelayBotChat}
      >
        {t("betting.depositGift")}
      </Button>
    </div>
  )

  const presetsBusy = activeIsProcessing

  const isPresetAvailable = (preset: number) => {
    if (!isTonMode || !walletConnected || maxDepositNano == null) return true
    return tonPresetToNano(preset) <= maxDepositNano
  }

  useEffect(() => {
    if (selectedPreset === null || isMaxPreset) return
    if (!isTonMode || !walletConnected || maxDepositNano == null) return
    if (tonPresetToNano(selectedPreset) > maxDepositNano) {
      setSelectedPreset(null)
    }
  }, [selectedPreset, isMaxPreset, maxDepositNano, isTonMode, walletConnected])

  const formatPresetLabel = (n: number) =>
    Number.isInteger(n) ? String(n) : n.toFixed(1)

  const presetsRow = (
    <div
      className={cn(
        "flex flex-row items-center gap-1 px-0",
        presetsBusy && "pointer-events-none opacity-40"
      )}
    >
      {PRESETS.map((preset) => {
        const available = isPresetAvailable(preset)
        return (
        <button
          key={preset}
          type="button"
          onClick={() => handlePreset(preset)}
          disabled={presetsBusy || !available}
          className={cn(
            "flex min-h-0 flex-1 flex-row items-center justify-center rounded-full px-4 py-[10px] text-[16px] font-medium leading-[19px] tracking-[-0.5px] text-white transition-colors",
            "bg-white/10 active:opacity-90 disabled:opacity-30"
          )}
        >
          {formatPresetLabel(preset)}
        </button>
        )
      })}
      {isTonMode && (
        <button
          type="button"
          onClick={handleMax}
          disabled={
            presetsBusy || maxDepositNano == null || maxDepositNano <= 0
          }
          className={cn(
            "flex min-h-0 flex-1 flex-row items-center justify-center rounded-full px-4 py-[10px] text-[16px] font-medium leading-[19px] tracking-[-0.5px] text-white transition-colors",
            "bg-white/10 active:opacity-90 disabled:opacity-30"
          )}
        >
          {t("common.max")}
        </button>
      )}
    </div>
  )

  // Non-TON providers and TON with wallet: show deposit CTA.
  // TON without wallet: show connect-wallet CTA.
  const cta =
    !isTonMode || walletConnected ? (
      <Button
        appearance="green"
        size="medium"
        isLoading={activeIsProcessing}
        disabled={!hasValidAmount || activeIsProcessing}
        onClick={handleDeposit}
      >
        {hasValidAmount && effectiveAmountNano != null
          ? t("deposit.action", {
              amount: formatTonDepositDisplay(effectiveAmountNano),
            })
          : t("common.enterAmount")}
      </Button>
    ) : (
      <Button
        appearance="green"
        size="medium"
        isLoading={depositProcessing}
        disabled={depositProcessing}
        onClick={() => tonConnectUI.openModal()}
      >
        {t("common.connectWallet")}
      </Button>
    )

  return (
    <BaseAmountSheet
      open={isOpen}
      onClose={() => setIsOpen(false)}
      isProcessing={activeIsProcessing}
      title={t("deposit.title")}
      balance={formatBalance(balanceAmountStr)}
      address={
        isTonMode && !isGiftsTab ? (connectedAddress ?? undefined) : undefined
      }
      walletBalance={isTonMode && !isGiftsTab ? walletBalanceTon : null}
      amountWarning={isTonMode && !isGiftsTab ? amountWarning : null}
      amountIsWarning={!!amountWarning}
      onDisconnect={
        isTonMode && !isGiftsTab && walletConnected
          ? () => tonConnectUI.disconnect()
          : undefined
      }
      amountEditable={!isTonMode || walletConnected}
      amountInputRef={amountInputRef}
      inputDisplayValue={inputDisplayValue}
      hasValidAmount={hasValidAmount}
      amountLabel={
        hasValidAmount ? t("common.amount") : t("common.enterAmount")
      }
      onAmountChange={handleInputChange}
      onAmountKeyDown={handleAmountKeyDown}
      topContent={topContent}
      body={isGiftsTab ? giftBody : undefined}
      presets={isGiftsTab || !(!isTonMode || walletConnected) ? null : presetsRow}
      cta={isGiftsTab ? giftCta : cta}
    />
  )
}
